/**
 * ⚖ D-71 — telling "this case is GONE" apart from "I could not load it".
 *
 * Found when a sweep deleted a case out from under an open tab: the pane
 * rendered a generic *"Falha ao carregar a área de trabalho do caso"*, the
 * header said *"Caso sem título"*, and — the serious half — **the composer went
 * on accepting uploads**, six of them, with no receipt and no error. The lawyer
 * believes the document was filed.
 *
 * ---
 *
 * ## ⚠ The rule this file exists to hold
 *
 * **An unclassifiable error falls back to RETRYABLE, never to DELETED.**
 *
 * "This case no longer exists" is the claim that cannot be walked back. A
 * lawyer told that will stop looking for their work — so a transient gateway
 * blip must never render as a deletion. The reverse mistake (calling a real
 * deletion "temporary") is recoverable: they retry, and find out.
 *
 * That asymmetry is the whole design. It is the same unknown-vs-negative rule
 * this workstream has been applying everywhere else, and fixing the banner is
 * exactly where it would be easy to introduce the mirror defect.
 */

/**
 * - `"gone"`      — the server said, specifically, that the case is not there.
 * - `"retired"`   — the server said, specifically, that it is in the recycle
 *                   bin. A KNOWN state with a remedy, not a failure.
 * - `"retryable"` — anything else, INCLUDING an error we cannot classify.
 * - `"ok"`        — no error.
 */
export type CaseReachability = "ok" | "gone" | "retired" | "retryable";

/**
 * Gateway error codes that mean "this case is not there" — and ONLY those.
 *
 * ⚠ Deliberately a closed allow-list read from the wire's own vocabulary, NOT
 * a pattern match on the message. A message is prose that gets reworded; a
 * code is a contract. And a broadened match here does not degrade gracefully —
 * it converts transient failures into an irreversible-sounding claim.
 */
const GONE_CODES: ReadonlySet<string> = new Set(["NOT_FOUND", "CASE_NOT_FOUND"]);

/**
 * Codes that mean "soft-deleted, sitting in the recycle bin, restorable".
 *
 * ⚖ Shipped by `lexruntime-changes` 2026-09-08 (`dba60048b`,
 * `case-retired-error-code-miniplan.md`) at this lane's request. Before it, the
 * distinction existed only as PROSE in `assertCaseNotRetired`'s thrown message,
 * so a retired case fell to `retryable` and the lawyer read *"Falha ao carregar
 * a área de trabalho do caso"* — the product looking broken when nothing was
 * wrong. Operator-surfaced; found by `ui-tester` with a live-case control.
 *
 * ⚠ Same closed-allow-list rule as `GONE_CODES`, for the same reason: never
 * pattern-match the message. The runtime's own classifier now reads `.code`
 * first and keeps message-matching only for UNDECLARED throws, so this set is
 * one end of a real contract rather than a guess about wording.
 *
 * ⚠ This is a THIRD state, not a synonym for `gone`. "Este caso não existe
 * mais" would be FALSE of a retired case — it exists, intact, and an org admin
 * can restore it. Collapsing the two would trade one lie for another.
 */
const RETIRED_CODES: ReadonlySet<string> = new Set(["CASE_RETIRED"]);

interface MaybeCoded {
  code?: unknown;
  message?: unknown;
}

/**
 * Classify a workspace-load failure.
 *
 * Accepts `unknown` because React Query hands back `Error`, and the gateway's
 * error envelope rides on it in different shapes depending on the transport.
 * Anything it cannot read confidently is `retryable` — see the file docblock.
 */
export function classifyCaseError(error: unknown): CaseReachability {
  if (!error) return "ok";

  const codeOf = (v: unknown): string | undefined => {
    if (!v || typeof v !== "object") return undefined;
    const c = (v as MaybeCoded).code;
    return typeof c === "string" ? c : undefined;
  };

  // The code may sit on the error itself or on a nested `cause` — never on a
  // parsed message.
  const direct = codeOf(error);
  const nested = codeOf((error as { cause?: unknown }).cause);
  const code = direct ?? nested;

  if (code && GONE_CODES.has(code)) return "gone";
  if (code && RETIRED_CODES.has(code)) return "retired";
  return "retryable";
}

/**
 * May the composer accept an upload right now?
 *
 * ⚠ **`retryable` returns FALSE too.** This is not the affordance-layer rule
 * where unknown permits — an upload is a WRITE the lawyer will believe
 * succeeded, so the safe direction inverts: if we cannot confirm the case is
 * reachable, we must not silently swallow their file. Refusing costs a retry;
 * accepting costs a document they think is filed and is not.
 *
 * ⚠ `retired` returns FALSE for the same reason, and this is why the predicate
 * is written as `=== "ok"` rather than as a deny-list: adding a state to
 * `CaseReachability` must never accidentally open the upload door. A retired
 * case is soft-deleted — the runtime refuses to materialize it — so a file
 * accepted here would land nowhere while looking filed.
 */
export function canAcceptUpload(reach: CaseReachability): boolean {
  return reach === "ok";
}
